import {
    AnimationBaseController,
    AnimationTextStyle,
    IAnimationControllerConfiguration,
    Inject,
    ModelEvents,
    SoundType,
} from '@denishussar/html5-game-engine';
import { AnimationContents } from '../../../AnimationContents';
import { AppStateEnum, GameModel } from '../../Models/GameModel';
import { GameLogic } from '../../Logic/GameLogic';
import { ButtonController, IButtonControllerOptions } from '../../Common/ButtonController';
import {
    GarageScreenTabShopItemController as GarageTabItemController,
    GarageSlideEnum,
    IGarageScreenTabShopItemControllerOptions as IGarageTabItemOptions,
    StateEnum,
} from './GarageScreenTabShopItemController';
import { getAvailableUpgradeBoost, getCarConfig, getCommonConfig, getUpgradeConfig } from '../../Common/GameConfig';
import { remapValue } from '../../Common/Utils';
import { Sounds } from '../../Constants/Sounds';
import { SoundModel } from '../../Models/SoundModel';
import { GarageEvents } from '../../Constants/Events';
import { GarageLogic } from '../../Logic/GarageLogic';
import { CarModel } from '../../Models/CarModel';
import { GarageMainButton } from './GarageMainButton';
import { SettingsButtonController } from '../Settings/SettingsButton';
import { LocalizationContents } from '../../LocalizationContents';
import { COIN64 } from '../../Constants/ImageConstants';
import { thousandSpacing } from '../../Utils/StringRework';
import { Rectangle } from 'pixi.js';
import { resizeScreen } from '../../Utils/Resize';

export interface IGarageScreenControllerOptions {
}


export class GarageScreenController extends AnimationBaseController {
    $model: GameModel;

    public constructor(
        @Inject private _options: IGarageScreenControllerOptions,
        @Inject private _gameLogic: GameLogic,
        @Inject private garageLogic: GarageLogic,
        @Inject private _modelEvents: ModelEvents,
        @Inject private soundLevels: SoundModel,
    ) {
        super('GarageScreenController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        const events = {};
        events[GarageEvents.buyUpgrade] = () => {
            this.updateCars();
            this.soundManager.playSound(Sounds.TUNING, 0, SoundModel.UI);
        };
        events[GarageEvents.buyPaint] = () => {
            this.updateCars();
            this.soundManager.playSound(Sounds.TUNING, 0, SoundModel.UI);
        };
        events[GarageEvents.buyCar] = () => {
            this.updateCars();
            this.soundManager.playSound(Sounds.PURCHASE, 0, SoundModel.UI);
        };
        events[GarageEvents.repairCar] = () => this.updateCars();

        const children = {};
        children['btn_back'] = {
            instanceName: 'btn_back',
            controller: ButtonController,
            model: this.$model,
            options: <IButtonControllerOptions>{
                onClick: () => {
                    this._gameLogic.setState(AppStateEnum.leaderboardFromGarage);
                },
            },
        };
        children['btn_garage'] = {
            instanceName: 'btn_in_garage',
            controller: GarageMainButton,
            model: this.$model,
        };
        children['btn_left'] = {
            instanceName: 'btn_left',
            controller: ButtonController,
            model: this.$model,
            options: <IButtonControllerOptions>{
                onClick: () => {
                    if (this.$model.garage_selectedCar != 0)
                        this.garageLogic.selectCar(this.$model.garage_selectedCar - 1);
                    else
                        this.garageLogic.selectCar(this.$model.garage_maxCar - 1);
                    if (!this.$model.currentPlayer.isBought)
                        this.$model.garage_currentTab = 0;
                },
            },
        };
        children['btn_right'] = {
            instanceName: 'btn_right',
            controller: ButtonController,
            model: this.$model,
            options: <IButtonControllerOptions>{
                onClick: () => {
                    if (this.$model.garage_selectedCar != (this.$model.garage_maxCar - 1))
                        this.garageLogic.selectCar(this.$model.garage_selectedCar + 1);
                    else
                        this.garageLogic.selectCar(0);
                    if (!this.$model.currentPlayer.isBought)
                        this.$model.garage_currentTab = 0;
                },
            },
        };
        children['btn_cars'] = {
            instanceName: 'tabs_scroll.tab1',
            controller: ButtonController,
            options: {
                onClick: () => {
                    this.$model.garage_currentTab = 0;
                },
                notAnimated: true,
            },
        };
        children['btn_upgrade'] = {
            instanceName: 'tabs_scroll.tab2',
            controller: ButtonController,
            options: {
                onClick: () => {
                    if (this.$model.currentPlayer.isBought)
                        this.$model.garage_currentTab = 1;
                    else {
                        const controller = this.$childControllers.get('btn_garage') as GarageMainButton;
                        controller.updateMainButton();
                    }
                },
                notAnimated: true,
            },
        };
        children['btn_paint'] = {
            instanceName: 'tabs_scroll.tab3',
            controller: ButtonController,
            options: {
                onClick: () => {
                    if (this.$model.currentPlayer.isBought)
                        this.$model.garage_currentTab = 2;
                    else {
                        const controller = this.$childControllers.get('btn_garage') as GarageMainButton;
                        controller.updateMainButton();
                    }
                },
                notAnimated: true,
            },
        };
        for (let i = 0; i < this.$model.garage_maxCar; i++) {
            children['car_item_' + i] = {
                instanceName: 'tabs.tab_cars.item' + (i + 1),
                controller: GarageTabItemController,
                options: <IGarageTabItemOptions>{
                    type: GarageSlideEnum.car,
                    index: i,
                    state: StateEnum.locked,
                    onClick: () => {
                        this.garageLogic.selectCar(i);
                    },
                },
                model: this.$model,
            };
        }
        for (let i = 0; i <= this.$model.garage_maxUpgrades; i++) {
            children['upgrade_item_' + i] = {
                instanceName: 'tabs.tab_improvements.item' + (i + 1),
                controller: GarageTabItemController,
                options: <IGarageTabItemOptions>{
                    type: GarageSlideEnum.upgrade,
                    index: i,
                    state: StateEnum.locked,
                    onClick: () => {
                        if (i < this.$model.garage_maxUpgrades)
                            this.garageLogic.selectUpgrade(i);
                    },
                },
                model: this.$model,
            };
        }
        for (let i = 0; i < this.$model.garage_maxPaint; i++) {
            children['paint_item_' + i] = {
                instanceName: 'tabs.tab_colors.item' + (i + 1),
                controller: GarageTabItemController,
                options: <IGarageTabItemOptions>{
                    type: GarageSlideEnum.paint,
                    index: i,
                    state: StateEnum.locked,
                    onClick: () => {
                        this.garageLogic.selectPaint(i);
                    },
                },
                model: this.$model,
            };
        }


        return <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.GARAGE_SCREEN_JSON,
            libraryName: 'garage_screen',
            sounds: [
                {
                    id: Sounds.PURCHASE,
                    type: SoundType.SFX,
                },
                {
                    id: Sounds.TUNING,
                    type: SoundType.SFX,
                },
            ],
            childControllers: children,
            modelWatch: {
                coins: (value: number) => {
                    this.updateCoins();
                    this.switchTabs();
                },
                garage_selectedUpgrade: (upgrade: number) => {

                    this.updateCars();
                },
                garage_selectedPaint: (paint: number) => {

                    this.updateCars();
                },
                currentPlayer: (player: CarModel) => {
                    this.updateCars();
                    this.switchTabs();
                    this.$model.garage_selectedPaint = player.paint;
                },
                garage_currentTab: (index: number) => {

                    this.switchTabs();
                    this.updateCars();
                },

            },
            modelEvents: events,
        };
    }

    protected onInitialize(): void {

        if (this.soundManager.isSoundPlaying(Sounds.MUSIC_TRACK_1)) {
            this.soundManager.stop(Sounds.MUSIC_TRACK_1);
        }
        if (this.soundManager.isSoundPlaying(Sounds.MUSIC_TRACK_2)) {
            this.soundManager.stop(Sounds.MUSIC_TRACK_2);
        }
        if (!this.soundManager.isSoundPlaying(Sounds.MUSIC_MENU)) {
            this.soundManager.play(Sounds.MUSIC_MENU, true, SoundModel.music);
        }
        this.gotoAndStopByQuery('progress_acceleration', 1);
        this.gotoAndStopByQuery('progress_gas_rate', 2);
        this.gotoAndStopByQuery('tabs.tab_improvements.item4', 3);
        this.updateCars();
        this.updateCoins();
        this.switchTabs();

        this.addChildControllerAsync({
            controller: SettingsButtonController,
            model: this.$model,
        }, false, 'settingsButton');

        //workaround for upgrades tabs
        this.garageLogic.selectUpgrade(1);
        this.garageLogic.selectUpgrade(0);

        const coin64: AnimationTextStyle = {
            imgSrc: COIN64,
            height: 56,
            width: 58,
            valign: 'middle',
            padding: 10,
            fontSize: 64,
        };

        this.setTextStyleByQuery('txt_coins', { valign: 'middle' }, { coin: coin64 });

        this.getDisplayObjectByQuery('btn_right')[0].hitArea = new Rectangle(-80, -90, 160, 180);
        this.getDisplayObjectByQuery('btn_left')[0].hitArea = new Rectangle(-80, -90, 160, 180);
    }

    protected onResize(width: number, height: number, scale: number): void {
        resizeScreen(width, height, scale, this);
        // this.scaleX = this.scaleY = Math.min(
        //     width / scale / this.$model.gameWidth,
        //     height / scale / this.$model.gameHeight,
        // );
    }


    protected onUpdate(frameRate: number): void {
    }

    private updateCoins(): void {
        let coins = this._gameLogic.getCoins();
        let k = '';
        while (coins > 99999) {
            coins /= 1000;
            k += 'k';
        }
        this.setTextVerticalAlignByQuery('txt_coins', 'middle');
        this.setTextByQuery('txt_coins', '<coin> </coin>    ' + thousandSpacing(Math.floor(coins)) + k + ' ');
    }

    private switchTabs(index: number = this.$model.garage_currentTab) {
        // this.gotoAndStopByQuery('tabs_scroll', index);
        this.gotoAndStopByQuery('tabs', index);
        switch (index) {
            case 0:
                this.gotoAndStopByQuery('tabs_scroll.tab1', 0);
                if (!this.$model.currentPlayer.isBought) {
                    this.gotoAndStopByQuery('tabs_scroll.tab2', 2);
                    this.gotoAndStopByQuery('tabs_scroll.tab3', 2);
                } else {
                    this.gotoAndStopByQuery('tabs_scroll.tab2', 1);
                    this.gotoAndStopByQuery('tabs_scroll.tab3', 1);
                }
                break;
            case 1:
                this.gotoAndStopByQuery('tabs_scroll.tab1', 1);
                this.gotoAndStopByQuery('tabs_scroll.tab2', 0);
                this.gotoAndStopByQuery('tabs_scroll.tab3', 1);
                break;

            case 2:
                this.gotoAndStopByQuery('tabs_scroll.tab1', 1);
                this.gotoAndStopByQuery('tabs_scroll.tab2', 1);
                this.gotoAndStopByQuery('tabs_scroll.tab3', 0);
                break;
        }
    }

    private updateCars(index: number = this.$model.garage_selectedCar): void {
        const car = this.$model.currentPlayer;
        this.gotoAndStopByQuery('icons_cars_garage', index);

        const speedMax = getCommonConfig('speedMax');
        const accelerationMax = getCommonConfig('accelerationMax');
        const gasRateMax = getCommonConfig('gasRateMax');

        const upgradeConfig = getAvailableUpgradeBoost(car);

        const upgradeSpeed = upgradeConfig.speed;
        const upgradeAcceleration = upgradeConfig.acceleration;
        const upgradeGasRate = upgradeConfig.gasRate;

        const carConfig = getCarConfig(this.$model.currentPlayer.index);

        const speed = carConfig.speed + upgradeSpeed;
        const acceleration = carConfig.acceleration + upgradeAcceleration;
        const gasRate = carConfig.gasRate + upgradeGasRate;

        const speedProgress = remapValue(speed, 0, speedMax, 0, 23);
        const accelerationProgress = remapValue(acceleration, 0, accelerationMax, 0, 23);
        const gasRateProgress = remapValue(gasRate, 0, gasRateMax, 0, 23);

        this.gotoAndStopByQuery('progress_speed.progress_bar_speed.massk_progress', Math.round(speedProgress));
        this.gotoAndStopByQuery('progress_acceleration.progress_bar_acceleration.massk_progress', Math.round(accelerationProgress));
        this.gotoAndStopByQuery('progress_bar_damage', Math.round(remapValue(this.$model.currentPlayer.health, 0, 100, 0, 43)));

        if ((this.$model.garage_currentTab == 1) && (!this.$model.currentPlayer.upgrades[this.$model.garage_selectedUpgrade]) &&
            (car.isBought)) {
            let speedBoost = remapValue(speed + getUpgradeConfig(this.$model.garage_selectedUpgrade, 'maxSpeed'), 0, speedMax, 0, 23);
            let accelBoost = remapValue(acceleration + getUpgradeConfig(this.$model.garage_selectedUpgrade, 'acceleration'), 0, accelerationMax, 0, 23);
            let gasBoost = remapValue(gasRate + getUpgradeConfig(this.$model.garage_selectedUpgrade, 'fuelRate'), 0, gasRateMax, 0, 23);

            this.gotoAndStopByQuery('progress_speed.progress_bar_speed.massk_difference', Math.round(speedBoost));
            this.gotoAndStopByQuery('progress_acceleration.progress_bar_acceleration.massk_difference', Math.round(accelBoost));
            this.gotoAndStopByQuery('progress_gas_rate.progress_bar_fuel_consumption.massk_difference', Math.round(gasRateProgress));
            this.gotoAndStopByQuery('progress_gas_rate.progress_bar_fuel_consumption.massk_progress', Math.round(gasBoost));
        } else {
            this.gotoAndStopByQuery('progress_speed.progress_bar_speed.massk_difference', 0);
            this.gotoAndStopByQuery('progress_acceleration.progress_bar_acceleration.massk_difference', 0);
            this.gotoAndStopByQuery('progress_gas_rate.progress_bar_fuel_consumption.massk_difference', 0);
            this.gotoAndStopByQuery('progress_gas_rate.progress_bar_fuel_consumption.massk_progress', Math.round(gasRateProgress));
        }

        if ((this.$model.garage_currentTab != 2) && (this.$model.currentPlayer.isBought))
            this.gotoAndStopByQuery('icons_cars_garage.car', car.paint);
        else
            this.gotoAndStopByQuery('icons_cars_garage.car', this.$model.garage_selectedPaint);

        this.updateTranslations();

    }

    updateTranslations() {
        this.setTextByQuery('progress_speed.progress_bar_speed.title_stat_1_txt', ' ' + LocalizationContents.getText('title_stat_1'));
        this.setTextByQuery('progress_acceleration.progress_bar_acceleration.title_stat_2_txt', ' ' + LocalizationContents.getText('title_stat_2'));
        this.setTextByQuery('progress_gas_rate.progress_bar_fuel_consumption.title_stat_3_txt', LocalizationContents.getText('title_stat_3') + ' ');
        this.setTextByQuery('txt_title', LocalizationContents.getText(getCarConfig(this.$model.garage_selectedCar, 'title')));
    }


}
